/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
// Display/formatting + comparison-math helpers for the Report Builder.
//
// Extracted verbatim from AIReportBuilder.tsx so they can be unit-tested in
// isolation — they are pure (no React/DOM) and depend only on the shared
// Countries list. The component imports them back from './reportFormat'.
//
// The agent emits metric aliases already human-readable ("Duty Cost"), but
// group_by columns arrive as raw snake_case ("country_of_origin") because the
// tool schema has no alias slot for them. These helpers humanize headers and
// resolve ISO-2 country codes to full names purely at render time.
//
// NOTE: the Countries import is intentionally RELATIVE (not the 'components/...'
// CRA path alias) so this module also resolves under the Playwright test runner,
// which has no CRA `baseUrl` configured.
import { countries } from '../../components/Common/Countries';

// O(1) ISO-2 code → country name lookup, built once from the shared Countries list.
export const COUNTRY_BY_CODE = new Map<string, string>(
  countries.map((c) => [c.code.toUpperCase(), c.label]),
);

// Explicit labels for columns whose generic title-casing would read poorly.
const KNOWN_HEADER_LABELS: Record<string, string> = {
  country_of_origin: 'Country of Origin',
  country_of_origin_code: 'Country of Origin',
  country_of_origin_name: 'Country of Origin',
  country_of_export_name: 'Country of Export',
  country_of_export_code: 'Country of Export',
  hts_code: 'HTS Code',
  hts_number_full: 'HTS Code',
  hts_short_description: 'HTS Description',
};

// Words kept lowercase mid-phrase, and tokens upper-cased as acronyms.
const HEADER_CONNECTORS = new Set([
  'of',
  'by',
  'the',
  'and',
  'per',
  'to',
  'for',
  'in',
]);
const HEADER_ACRONYMS = new Set([
  'hts',
  'mpf',
  'hmf',
  'ace',
  'fta',
  'ieepa',
  'iso',
  'id',
]);

// Turn a column key into a human-readable header. Agent metric aliases already
// contain spaces ("Duty Cost") and pass through untouched.
export const humanizeHeader = (col: string): string => {
  if (col.includes(' ')) return col;
  const known = KNOWN_HEADER_LABELS[col.toLowerCase()];
  if (known) return known;
  return col
    .split('_')
    .map((word, idx) => {
      const lower = word.toLowerCase();
      if (HEADER_ACRONYMS.has(lower)) return word.toUpperCase();
      if (idx > 0 && HEADER_CONNECTORS.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
};

// Resolve cell values: country columns get code→name conversion (lookup-or-
// passthrough, so already-full names survive). Everything else is unchanged.
export const formatCellValue = (col: string, val: any): string => {
  if (val === undefined || val === null) return '';
  if (col.toLowerCase().includes('country')) {
    return COUNTRY_BY_CODE.get(String(val).toUpperCase()) ?? String(val);
  }
  return String(val);
};

// Comparison columns the backend emits verbatim; used for currency formatting and
// delta coloring in the comparison view.
export const COMPARISON_DUTY_COLUMNS = new Set([
  'Historical Duty',
  'Projected Duty',
  'Delta',
]);
// All fixed metric columns (currency + the percent). Anything NOT in here is a
// dimension column (e.g. "Country of Origin"). A grand-total comparison
// (dimensions: []) has no dimension column.
export const COMPARISON_METRIC_COLUMNS = new Set([
  ...COMPARISON_DUTY_COLUMNS,
  'Delta %',
]);

// Render a duty figure as USD. Non-numeric values (e.g. a NULL "Delta %") pass through.
export const formatCurrency = (val: any): string => {
  const n = Number(val);
  if (val === null || val === undefined || val === '' || Number.isNaN(n))
    return '';
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
};

// Cell text for the comparison view: duty columns as currency, "Delta %" with a
// sign and % suffix, everything else via the standard value formatter.
export const comparisonCellText = (
  mode: 'comparison' | undefined,
  col: string,
  val: any,
): string => {
  if (mode === 'comparison') {
    if (COMPARISON_DUTY_COLUMNS.has(col)) return formatCurrency(val);
    if (col === 'Delta %') {
      const n = Number(val);
      if (val === null || val === undefined || val === '' || Number.isNaN(n))
        return '—';
      return `${n > 0 ? '+' : ''}${n}%`;
    }
  }
  return formatCellValue(col, val);
};

// Identifier columns (HTS, product/entry codes, line/entry numbers) hold codes, NOT
// quantities — their decimal-looking values must never be coerced through Number()/rounding,
// which would corrupt them (e.g. the HTS display "0101.21" → 101.21, dropping the leading
// zero). None of the numeric metric columns contain these tokens, so this is a safe skip.
export const isIdentifierColumn = (col: string): boolean => {
  const c = col.toLowerCase();
  return c.includes('hts') || c.includes('code') || c.includes('number');
};

// Round numeric values to at most 3 decimal places FOR ON-SCREEN DISPLAY ONLY.
// Some metrics (e.g. duty figures) come back with 10+ decimals and bloat the table.
// The CSV export (handleDownloadCSV) is deliberately left at full precision — this
// only shortens what the grid shows. Identifier columns, integers, dates, and free text
// pass through untouched (only pure decimal strings / numbers in value columns are rounded).
export const roundForDisplay = (col: string, val: any): any => {
  if (isIdentifierColumn(col)) return val;
  if (typeof val === 'number') {
    return Number.isInteger(val) ? val : Math.round(val * 1000) / 1000;
  }
  if (typeof val === 'string' && /^-?\d+\.\d+$/.test(val.trim())) {
    return Math.round(Number(val.trim()) * 1000) / 1000;
  }
  return val;
};

// Table-cell text: identical to comparisonCellText but with display-only decimal
// rounding applied first. Used by the preview grid; NOT by the CSV export.
export const formatCellDisplay = (
  mode: 'comparison' | undefined,
  col: string,
  val: any,
): string => comparisonCellText(mode, col, roundForDisplay(col, val));

// Aggregate the comparison rows into the figures both the table's totals row and
// the Calculated Metrics KPIs need: total historical/projected duty, the net dollar
// change, and the overall percentage change (null when there's no historical base).
export const computeComparisonTotals = (
  rows: any[],
): {
  totalHistorical: number;
  totalProjected: number;
  netChange: number;
  overallPct: number | null;
} => {
  const totalHistorical = rows.reduce(
    (acc, r) => acc + (Number(r['Historical Duty']) || 0),
    0,
  );
  const totalProjected = rows.reduce(
    (acc, r) => acc + (Number(r['Projected Duty']) || 0),
    0,
  );
  const netChange = totalProjected - totalHistorical;
  const overallPct =
    totalHistorical === 0
      ? null
      : Math.round((netChange / totalHistorical) * 1000) / 10;
  return {
    totalHistorical: totalHistorical,
    totalProjected: totalProjected,
    netChange: netChange,
    overallPct: overallPct,
  };
};
